import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import FeaturedIconComponent from 'Component/FeaturedIcon';
import {
    BLACK_KEY, XS_KEY
} from 'Component/Icon/Icon.config';
import Label from 'Component/Label';
import {
    COLOR_BLACK, COLOR_WHITE, COLOR_WHITE_AND_RED,
    COLOR_YELLOW_AND_BLACK, GAP_LARGE, GAP_SMALL, PADDING_SMALL 
} from 'Component/Label/Label.config';
import LabelContainer from 'Component/LabelContainer';
import { TOP_LC } from 'Component/LabelContainer/LabelContainer.config';
import { ProductType } from 'Type/ProductList.type';
import {
    getPrice,
    getProductInStock
} from 'Util/Product/Extract';

/** @namespace Bodypwa/Component/ProductLabels/Component/ProductLabels */
export function ProductLabels({
    product = {},
    discount = 0,
    isCampaingLabel = true,
    classPriceDiscount = '',
    isShowOnlyUspLabels = false,
    isShowUspLabels = true,
    colorCampaign = COLOR_WHITE_AND_RED,
    isShowOOSLabel = false,
    size = PADDING_SMALL,
    position = '',
    isBorder = false,
    gapSize = GAP_SMALL,
    isFeaturedProduct
}) {
    
    const inStock = getProductInStock(product);
    const showFeaturedProductLabel = useSelector((state) => state.ConfigReducer.showFeaturedProductLabel);
    const gapClass = {
        [GAP_LARGE]: 'gap-2',
        [GAP_SMALL]: 'gap-1'
    };

    console.log(showFeaturedProductLabel, 'showFeaturedProductLabelshowFeaturedProductLabel');
    const renderFeaturedLabel = () => {
        if (!isFeaturedProduct) {
            return null;
        }

        return (
            <Label
                icon={ <FeaturedIconComponent size={ XS_KEY } color={ BLACK_KEY } /> }
                text={ __('Bestseller') }
                color={ COLOR_YELLOW_AND_BLACK }
             />
        );
    };

    const campaignLabel = useMemo(() => {
        const {
            price_range: priceRange,
            dynamic_price: dynamicPrice,
            type,
            attributes
        } = product || {};

        const { campaign_label: { attribute_value, attribute_options } = {} } = attributes || {};
        const { price: { discount: { percentOff } = {} } = {} } = getPrice(priceRange, dynamicPrice, {}, type) || {};

        const campaignLabel = attribute_value
            ? attribute_options?.[attribute_value]?.label
            : '';

        if (!percentOff && !campaignLabel) {
            return null;
        }

        return campaignLabel || `-${Math.round(percentOff)}%`;
    }, [product]);

    const flashLabel = useMemo(() => {
        const { attributes } = product || {};
        const { flash_label } = attributes || {};

        const { attribute_value, attribute_options } = flash_label || {};

        if (!attribute_value || !attribute_options) {
            return null;
        }
        
        return attribute_options?.[attribute_value]?.label || null;
    }, [product]);

    const uspLabels = useMemo(() => {
        const { attributes: { product_label_usp: usp = {} } = {} } = product || {};
        const { attribute_options_order, attribute_options = {} } = usp;
        if (!attribute_options_order) {
            return [];
        }

        return attribute_options_order
            .map((value) => {
                const label = attribute_options?.[value]?.label;
                return label ? { key: value, label } : null;
            })
            .filter(Boolean);
    }, [product]);

    const selectedLabels = useMemo(() => {
        const MAX = 3;
        const items = [];

        if (campaignLabel && !isShowOnlyUspLabels) {
            items.push({ type: 'campaign', key: 'campaign', label: campaignLabel });
        }

        if (flashLabel && !isShowOnlyUspLabels) {
            items.push({ type: 'flash', key: 'flash', label: flashLabel });
        }
        

        if (isShowUspLabels) {
            for (let i = 0; i < uspLabels.length && items.length < MAX; i += 1) {
                const u = uspLabels[i];
                items.push({ type: 'usp', key: `usp-${u.key}`, label: u.label });
            }
        }

        return items;
    }, [campaignLabel, flashLabel, uspLabels, isShowOnlyUspLabels, isShowUspLabels]);

    const renderLabel = () => (
        <div className={ `flex flex-row ${gapClass[gapSize]}` }>
            { selectedLabels.map((item) => {
                const color = item.type === 'campaign'
                    ? colorCampaign : item.type === 'flash'
                        ? COLOR_WHITE_AND_RED
                        : COLOR_WHITE;
                const fontBold = item.type === 'campaign' || item.type === 'flash';
                return (
                    <Label
                        key={ item.key }
                        text={ item.label }
                        color={ color }
                        size={ size }
                        isFontBold={ fontBold }
                        id={ `${ item.key }` }
                        isBorder={ item.type === 'flash' && isBorder }
                        gapSize={ gapSize }
                    />
                );
            }) }
        </div>
    );

    if (!isCampaingLabel) {
        if (discount === 0) {
            return null;
        }

        return (
            <span block="ProductPrice" elem="PriceDiscount" className={ classPriceDiscount }>
                { __('You save %s', `${Math.round(discount)}%`) }
            </span>
        );
    }

    if (!inStock && isShowOOSLabel) {
        return (
            <Label
                key="stock"
                isFontBold
                text={ __('SOLD OUT') }
                color={ COLOR_BLACK }
            />
        );
    }

    if (selectedLabels.length === 0 && !isFeaturedProduct) {
        return null;
    }

    return (
        <>
            { isFeaturedProduct && showFeaturedProductLabel && (
                <LabelContainer position={ TOP_LC }>
                    { renderFeaturedLabel() }
                </LabelContainer>
            ) }
            { selectedLabels.length > 0 && (
                <LabelContainer position={ position }>
                    { renderLabel() }
                </LabelContainer>
            ) }
        </>
    );
}

ProductLabels.propTypes = {
    product: ProductType,
    discount: PropTypes.number,
    isCampaingLabel: PropTypes.bool,
    classPriceDiscount: PropTypes.string,
    isShowOnlyUspLabels: PropTypes.bool,
    isShowUspLabels: PropTypes.bool,
    colorCampaign: PropTypes.string,
    size: PropTypes.string,
    isShowOOSLabel: PropTypes.bool,
    position: PropTypes.string,
    isBorder: PropTypes.bool,
    gapSize: ProductType.string,
    isFeaturedProduct: PropTypes.bool
};

export default ProductLabels;
